import React, { FC, useMemo } from 'react';
import { useLocale } from '@app/core/hooks/useLocale';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import classNames from 'classnames';
import {
  OpmSampleWaterfallsDto,
  OpmSampleWaterfallInstrumentDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import SvgChevron from '@app/shared/icons/Chevron';
import { enumKeyByValue } from '@app/shared/helpers';
import { InstrumentType } from '@app/shared/models/contracts/enums/shared-enums';
import { ExpandableKeys, ExpandedSectionsState } from './SampleWaterfallOutputsSummary';
import { NOT_APPLICABLE } from './sample-waterfall-column-helpers';
import WaterfallValueCell from './WaterfallValueCell';

type InstrumentValueKeys = Omit<OpmSampleWaterfallInstrumentDto, 'tranches'>;

interface InterimCashFlowsSectionProps {
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>;
  columnKeys: string[];
  instruments: (OpmSampleWaterfallInstrumentDto & { instrumentId: string })[];
  onToggle: (key: string, subKey?: string) => void;
  expandedSections: ExpandedSectionsState;
}

const SampleWaterfallOutputsInterimCashFlowsSection: FC<InterimCashFlowsSectionProps> = ({
  waterfallResults,
  columnKeys,
  instruments,
  onToggle,
  expandedSections,
}) => {
  const { l } = useLocale();

  const hasNonZeroValue = (value: number | null | undefined) =>
    value !== undefined && value !== null && value !== 0;

  const createInstrumentRow = React.useCallback(
    (label: string, valueKey: keyof InstrumentValueKeys, instrumentId: string) => {
      const getValue = (columnKey: string) => {
        const instrument = waterfallResults[columnKey]?.instruments?.[instrumentId];
        return instrument ? instrument[valueKey] : NOT_APPLICABLE;
      };

      const isRequiredByAnyScenario = columnKeys.some((columnKey) => {
        const value = getValue(columnKey);
        return typeof value === 'number' && hasNonZeroValue(value);
      });

      if (!isRequiredByAnyScenario) {
        return;
      }

      return (
        <tr key={`${instrumentId}-${valueKey}`}>
          <td className={classNames(styles['instrument-indentation'], styles['border-right'])}>
            {label}
          </td>
          {columnKeys.map((key) => {
            const value = getValue(key);
            return (
              <WaterfallValueCell
                key={key}
                value={typeof value === 'number' || value === NOT_APPLICABLE ? value : null}
              />
            );
          })}
        </tr>
      );
    },
    [waterfallResults, columnKeys]
  );

  const instrumentHeaderRow = React.useCallback(
    (instrument: OpmSampleWaterfallInstrumentDto & { instrumentId: string }) => (
      <tr>
        <th
          className={classNames(styles['section-header'], styles['section-header--indented'])}
          colSpan={columnKeys.length + 1}>
          {!expandedSections.interimCashFlowsInstruments?.[instrument.instrumentId] ? (
            <div
              className={classNames(styles['expand-toggle'], styles['expand-toggle--instrument'])}>
              <SvgChevron
                onClick={() =>
                  onToggle(ExpandableKeys.InterimCashFlowsInstruments, instrument.instrumentId)
                }
                className={styles[`chevron-collapsed`]}
              />
            </div>
          ) : (
            <div
              className={classNames(styles['expand-toggle'], styles['expand-toggle--instrument'])}>
              <SvgChevron
                onClick={() =>
                  onToggle(ExpandableKeys.InterimCashFlowsInstruments, instrument.instrumentId)
                }
                className={styles[`chevron-expanded`]}
              />
            </div>
          )}
          {instrument.instrumentName}
        </th>
      </tr>
    ),
    [columnKeys.length, expandedSections.interimCashFlowsInstruments, onToggle]
  );

  const interimCashFlowSections = useMemo(() => {
    const hasAnyInterimCashFlows = (instrumentId: string, isOrdinaryEquity: boolean) =>
      columnKeys.some((columnKey) => {
        const instrument = waterfallResults[columnKey]?.instruments?.[instrumentId];
        if (!instrument) {
          return false;
        }

        const hasAddition = hasNonZeroValue(instrument.cumulativeAddition);
        const hasRedemption = hasNonZeroValue(instrument.cumulativeRedemption);

        if (isOrdinaryEquity) {
          return hasAddition || hasRedemption || hasNonZeroValue(instrument.cumulativeDividends);
        }

        return (
          hasAddition || hasRedemption || hasNonZeroValue(instrument.cumulativeCashPayInterest)
        );
      });

    return instruments
      .map((instrument) => {
        const isOrdinaryEquity =
          instrument.instrumentType ===
          enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity);

        if (hasAnyInterimCashFlows(instrument.instrumentId, isOrdinaryEquity)) {
          const isExpanded =
            expandedSections.interimCashFlowsInstruments?.[instrument.instrumentId];
          return (
            <React.Fragment key={instrument.instrumentId}>
              {instrumentHeaderRow(instrument)}
              {isExpanded && (
                <>
                  {createInstrumentRow(
                    l('_Infusion'),
                    'cumulativeAddition',
                    instrument.instrumentId
                  )}
                  {createInstrumentRow(
                    isOrdinaryEquity ? l('_Buyback') : l('_Redemption'),
                    'cumulativeRedemption',
                    instrument.instrumentId
                  )}
                  {!isOrdinaryEquity &&
                    createInstrumentRow(
                      l('_CashInterest'),
                      'cumulativeCashPayInterest',
                      instrument.instrumentId
                    )}
                  {isOrdinaryEquity &&
                    createInstrumentRow(
                      l('_Dividends'),
                      'cumulativeDividends',
                      instrument.instrumentId
                    )}
                </>
              )}
            </React.Fragment>
          );
        }
      })
      .filter(Boolean);
  }, [
    instruments,
    expandedSections.interimCashFlowsInstruments,
    instrumentHeaderRow,
    createInstrumentRow,
    waterfallResults,
    columnKeys,
    l,
  ]);

  return interimCashFlowSections.length > 0 ? (
    <>
      <tr>
        <th className={styles['section-header']} colSpan={columnKeys.length + 1}>
          {!expandedSections.interimCashFlows ? (
            <div className={styles['expand-toggle']}>
              <SvgChevron
                onClick={() => onToggle(ExpandableKeys.InterimCashFlows)}
                className={styles[`chevron-collapsed`]}
              />
            </div>
          ) : (
            <div className={styles['expand-toggle']}>
              <SvgChevron
                onClick={() => onToggle(ExpandableKeys.InterimCashFlows)}
                className={styles[`chevron-expanded`]}
              />
            </div>
          )}
          {l('_InterimCashFlows')}
        </th>
      </tr>
      {expandedSections.interimCashFlows && interimCashFlowSections}
    </>
  ) : null;
};

export default SampleWaterfallOutputsInterimCashFlowsSection;
